// =========================================================================
// Renewals, in words — the one place the Flow and Balances screens and the
// Devices & keys picker get their sentences from (slice E, 18 Oct 2026).
//
// The operator's rules (16–18 Oct 2026): a wallet is named for what it does
// (CLASS_WORD), the word is "renew", never "rotation", and a number groups
// en-US (1,008,212.5). Nothing here reads the server or the clock: every
// function takes what the API answered and gives back the text a screen
// prints, so the words have tests of their own and a change is one edit.
// =========================================================================

import { CLASS_WORD, groupDigits } from './custody-format';
import { fmtDay } from './format';
import { ApiError } from './errors';
import type { RenewalClass, RenewalRetiredRow } from './api';

/**
 * Which Ledger account (m/44'/195'/N') issues which class — the desk's one
 * convention, pinned to contracts/wallet.mjs KEY_ACCOUNT_CLASS by a test
 * (the Mini App cannot import the contract at build time). Account 0 is the
 * deposit key, whose first address is the operator's registered device; the
 * three floats renew from their own accounts.
 */
export const KEY_ACCOUNT_CLASS: Readonly<Record<number, string>> = Object.freeze({
  0: 'deposit', 1: 'running_hot', 2: 'dirty_hot', 3: 'treasury',
});

/** The classes that renew themselves — contracts RENEWABLE_CLASSES, pinned by a test. */
export const RENEWABLE_CLASSES: ReadonlyArray<RenewalClass> = Object.freeze(['running_hot', 'dirty_hot', 'treasury']);

/** The accounts the key picker offers: the four the desk has a use for. */
export const KEY_ACCOUNTS: ReadonlyArray<number> = Object.freeze([0, 1, 2, 3]);

/** The policy's bounds, as the server holds them (admin.custody_renewals.every_days 1..365). */
export const RENEWAL_DAYS_MIN = 1;
export const RENEWAL_DAYS_MAX = 365;

/** A whole number, grouped en-US: 1,008,212 — never 10,08,212. */
const count = (n: number): string => groupDigits(String(Math.trunc(n)));

/** "account 2" — a Ledger account, said as Ledger Live numbers it (from 1), for an index from 0. */
export function accountWord(index: number): string {
  return `account ${count(index + 1)}`;
}

/**
 * What the key picker says an account is for: "Account 1 · Customer deposits
 * (your device)", "Account 2 · Hot", … — and a plain "Account N" for one the
 * desk has no convention for.
 */
export function accountLabel(index: number): string {
  const cls = KEY_ACCOUNT_CLASS[index];
  const head = `Account ${count(index + 1)}`;
  if (cls === undefined) return head;
  if (cls === 'deposit') return `${head} · Deposit (your device)`;
  return `${head} · ${CLASS_WORD[cls] ?? cls}`;
}

/**
 * When the class renews, from the API's whole days until the next due:
 * "due now" at 0 or below, "renews in 1 day", "renews in N days" — and
 * "no renewal scheduled" when the server names no date.
 */
export function dueWord(dueInDays: number | null | undefined): string {
  if (dueInDays === null || dueInDays === undefined || !Number.isFinite(dueInDays)) return 'no renewal scheduled';
  const days = Math.trunc(dueInDays);
  if (days <= 0) return 'due now';
  if (days === 1) return 'renews in 1 day';
  return `renews in ${count(days)} days`;
}

/** The part of a renewal line the two screens share. */
export interface RenewalWords {
  class: RenewalClass;
  classWord: string;
  dueInDays: number | null;
  keyAccount: number;
  keyRegistered: boolean;
}

/**
 * One line per class, for the Flow screen and the Balances headers:
 *   "Hot · renews in 12 days · from account 2"
 *   "Merchant Hot · due now · from account 3"
 *   "Treasury · no key yet — register account 4"
 * The word comes from the register's class, not from the server's
 * `classWord`, so a server that renamed a class could not rename it on screen.
 */
export function renewalLine(r: RenewalWords): string {
  const word = CLASS_WORD[r.class] ?? r.classWord;
  if (!r.keyRegistered) return `${word} · no key yet — register ${accountWord(r.keyAccount)}`;
  return `${word} · ${dueWord(r.dueInDays)} · from ${accountWord(r.keyAccount)}`;
}

/** The question before "Renew now" — what happens, in one breath, so the tap is an informed one. */
export function renewConfirm(cls: RenewalClass): string {
  const word = CLASS_WORD[cls] ?? cls;
  return `Renew ${word} now? The next address is issued, the old one is retired and still watched, and its balance waits for your Ledger.`;
}

/** True for a decimal string above zero ("0.000001"); false for zero, null and anything unreadable. */
export function aboveZero(balance: string | null | undefined): boolean {
  if (typeof balance !== 'string') return false;
  const t = balance.trim();
  if (!/^\d+(\.\d+)?$/.test(t)) return false;
  return /[1-9]/.test(t);
}

/**
 * A retired address that still holds money, under its pool on Balances:
 *   "Retired 18 Oct · 1,001 USDT · move queued"          a move waits for the Ledger
 *   "Retired 18 Oct · 1,001 USDT to move"                 the orchestrator queues it on its next read
 *   "Retired 18 Oct · 1,001 USDT · waiting for a key you hold"
 *                                                          no key the desk holds can sign that address
 * The API's own words: an openMove is the queued move; keyRegistered false
 * with money on the row is the Nile Hot's case — a hand-registered address no
 * registered key signs. A row with no retirement date (a register row from
 * before the date was kept) is still "Retired".
 */
export function retiredLine(row: Pick<RenewalRetiredRow, 'retiredAt' | 'balance' | 'openMove' | 'signable'>, keyRegistered: boolean): string {
  const when = row.retiredAt ? `Retired ${fmtDay(row.retiredAt)}` : 'Retired';
  const amount = `${groupDigits(row.balance ?? '0')} USDT`;
  if (row.openMove !== null && row.openMove !== undefined) return `${when} · ${amount} · move queued`;
  // No key the operator holds signs for it (db/0087): the orchestrator said so once; this row says it from then on.
  if (row.signable === false) return `${when} · ${amount} · no key you hold — move by hand`;
  if (!keyRegistered) return `${when} · ${amount} · waiting for a key you hold`;
  return `${when} · ${amount} to move`;
}

/**
 * Why a renewal write was refused, for the person at the button — the
 * server's reason words (bff/src/routes/custody.ts, renewals), each as a
 * sentence; anything else as the API said it (lib/errors.ts).
 */
export function renewalRefusal(err: unknown): string {
  const api = err instanceof ApiError ? err : null;
  const reason = api !== null && typeof api.body.reason === 'string' ? api.body.reason : null;
  switch (reason) {
    case 'no_key_for_class': {
      const word = typeof api?.body.classWord === 'string' ? api.body.classWord : 'this class';
      const account = typeof api?.body.keyAccount === 'number' ? ` — register ${accountWord(api.body.keyAccount)} under Devices & keys first` : '';
      return `No key is registered for ${word}, so nothing could issue its next address${account}.`;
    }
    case 'every_days_invalid':
      return `The interval was not accepted: whole days, from ${RENEWAL_DAYS_MIN} to ${count(RENEWAL_DAYS_MAX)}.`;
    case 'unknown_class':
      return 'That is not a class that renews. Refresh the screen.';
    default:
      return String((err as { message?: unknown } | null)?.message ?? err);
  }
}

/**
 * Why a typed "every N days" cannot be saved, as a sentence — or null when
 * it can. The server checks again (1..365); this spares a round trip.
 */
export function everyDaysProblem(text: string): string | null {
  const t = String(text ?? '').trim();
  if (t === '') return 'Type how many days.';
  if (!/^\d+$/.test(t)) return 'Whole days only: 3, 15, 30.';
  const n = Number(t);
  if (n < RENEWAL_DAYS_MIN || n > RENEWAL_DAYS_MAX) return `Between ${RENEWAL_DAYS_MIN} and ${count(RENEWAL_DAYS_MAX)} days.`;
  return null;
}
